import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const AuthContext = createContext(null);
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading
  const [authed, setAuthed] = useState(undefined); // undefined=checking, true, false
  const [settings, setSettings] = useState({});

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data || {});
    } catch (e) {
      console.debug("Chargement des réglages échoué:", e);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("wp_token");
    if (!token) {
      setAuthed(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setAuthed(true);
      loadSettings();
    } catch {
      localStorage.removeItem("wp_token");
      setAuthed(false);
    }
  }, [loadSettings]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (username, pin) => {
    const { data } = await api.post("/auth/login", { username, pin });
    localStorage.setItem("wp_token", data.token);
    setUser(data.user);
    setAuthed(true);
    loadSettings();
    return data.user;
  };

  const logout = useCallback((reason) => {
    localStorage.removeItem("wp_token");
    setUser(null);
    setAuthed(false);
    if (reason === "idle") {
      toast.info("Déconnexion automatique après 5 minutes d'inactivité.");
    }
  }, []);

  // Auto-logout after 5 minutes of inactivity
  const idleTimer = useRef(null);
  useEffect(() => {
    if (authed !== true) return;
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => logout("idle"), IDLE_TIMEOUT_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [authed, logout]);

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ user, authed, isAdmin, login, logout, settings, loadSettings }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
